import React, { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { X, Clock, Settings, Check, Plus } from "lucide-react";
import Calendar from "@core/components/Calendar";
import type { Question } from "@core/components/polls/QuestionCard";
import { formatDateLocal } from "@core/lib/date-utils";
import { PollCreatorService } from "@core/services/PollCreatorService";
import type { TimeSlot } from "@core/services/PollCreatorService";
import { useDragToSelect } from "@core/hooks/useDragToSelect";

interface DateQuestionEditorWrapperProps {
  question: Question;
  onChange: (patch: Partial<Question>) => void;
  onClose: () => void;
  mergeMode?: boolean;
}

/**
 * Wrapper pour DateQuestionEditor dans l'app form-polls
 * Gère l'état mergeMode et le passe au DateQuestionEditor original
 */
export default function DateQuestionEditorWrapper({
  question,
  onChange,
  onClose,
  mergeMode = false,
}: DateQuestionEditorWrapperProps) {
  const { t } = useTranslation("forms");
  // Initialiser avec 2 mois visibles (mois actuel + mois suivant)
  const [visibleMonths, setVisibleMonths] = useState<Date[]>(() => {
    const now = new Date();
    const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return [currentMonth, nextMonth];
  });

  const selectedDates = useMemo(
    () => question.selectedDates || [],
    [question.selectedDates],
  );
  const timeSlotsByDate = useMemo(() => {
    const original = question.timeSlotsByDate || {};
    const converted: Record<string, TimeSlot[]> = {};

    Object.entries(original).forEach(([date, slots]) => {
      converted[date] = slots.map((slot) => ({
        ...slot,
        label: `${slot.hour.toString().padStart(2, "0")}:${slot.minute.toString().padStart(2, "0")}`,
      }));
    });

    return converted;
  }, [question.timeSlotsByDate]);
  const timeGranularity = question.timeGranularity || "30min";
  const allowMaybeVotes = question.allowMaybeVotes ?? false;
  const allowAnonymousVotes = question.allowAnonymousVotes ?? false;

  const granularityMinutes = useMemo(() => {
    switch (timeGranularity) {
      case "15min":
        return 15;
      case "30min":
        return 30;
      case "1h":
        return 60;
      default:
        return 30;
    }
  }, [timeGranularity]);

  const getTimeSlotBlocks = useCallback(
    (dateStr: string) => {
      const slots = timeSlotsByDate[dateStr] || [];
      return PollCreatorService.getTimeSlotBlocks(slots, granularityMinutes);
    },
    [timeSlotsByDate, granularityMinutes],
  );

  const handleTimeSlotToggle = useCallback(
    (dateStr: string, hour: number, minute: number) => {
      const newTimeSlotsByDate = PollCreatorService.handleTimeSlotToggle(
        dateStr,
        hour,
        minute,
        timeSlotsByDate,
        granularityMinutes,
        false, // includeDuration = false pour DateQuestionEditor
        mergeMode, // Passer mergeMode au service
      );
      onChange({
        timeSlotsByDate: newTimeSlotsByDate,
      });
    },
    [timeSlotsByDate, granularityMinutes, onChange, mergeMode],
  );

  // Hook drag-to-extend pour les horaires
  const { isDragging, handleDragStart, handleDragMove, handleDragEnd } =
    useDragToSelect({
      timeSlotsByDate,
      granularityMinutes,
    } as any);

  // Ajouter un créneau horaire vide si nécessaire
  const ensureTimeSlotExists = useCallback(
    (dateStr: string, hour: number, minute: number) => {
      const slots = timeSlotsByDate[dateStr] || [];
      const existingSlotIndex = slots.findIndex(
        (s) => s.hour === hour && s.minute === minute,
      );

      if (existingSlotIndex < 0) {
        const newSlot: TimeSlot = {
          hour,
          minute,
          enabled: true,
          label: `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`,
        };
        const newSlots = [...slots, newSlot];
        onChange({
          timeSlotsByDate: {
            ...timeSlotsByDate,
            [dateStr]: newSlots,
          },
        });
      }
    },
    [timeSlotsByDate, onChange],
  );

  return (
    <div className="bg-[#1e1e1e] rounded-lg border border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-[#0a0a0a]">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-blue-400" />
          <h3 className="text-lg font-medium text-white">
            {t("creator.date.header.title")}
          </h3>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
            aria-label={t("common.close")}
            data-testid="datequestioneditor-button"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Configuration des horaires pour chaque date */}
      {selectedDates.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-300">
                {t("creator.date.configureSlots")}
              </label>
            </div>
          </div>

          {/* Grille des horaires avec dates en colonnes */}
          <div className="border border-gray-700 rounded-lg bg-[#1e1e1e] overflow-hidden">
            {/* En-têtes des dates */}
            <div className="flex bg-[#0a0a0a]">
              <div className="w-16 h-8 flex-shrink-0 p-2 text-xs text-gray-300 flex items-center justify-center border-r border-gray-100">
                <Clock className="w-4 h-4" />
                {t("creator.date.hour")}
              </div>
              {selectedDates.map((dateStr, colIndex) => {
                const dateInfo =
                  PollCreatorService.formatSelectedDateHeader(dateStr);
                return (
                  <div
                    key={dateStr}
                    className={`
                      flex-1 
                      relative 
                      transition-colors 
                      hover:bg-[#2a2a2a] 
                      border-r 
                      border-gray-700
                      h-8
                       ${
                         colIndex === selectedDates.length - 1
                           ? "border-r-0"
                           : ""
                       }`}
                  >
                    <div className="text-center text-white font-medium">
                      {dateInfo.dayName}
                    </div>
                    <div className="text-center text-xs text-gray-400">
                      {dateInfo.dayNumber}
                    </div>
                    <div className="text-center text-xs text-gray-500">
                      {dateInfo.month}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Créneaux horaires */}
            <div
              className="max-h-96 overflow-y-auto"
              style={{ touchAction: "pan-y" }}
            >
              {/* En-têtes des créneaux */}
              <div className="flex bg-[#0a0a0a]">
                <div className="w-16 h-8 flex-shrink-0 p-2 text-xs text-gray-300 flex items-center justify-center border-r border-gray-100">
                  <Clock className="w-4 h-4" />
                  {t("creator.date.hour")}
                </div>
                {selectedDates.map((dateStr, colIndex) => (
                  <div
                    key={dateStr}
                    className={`
                      flex-1 
                      relative 
                      transition-colors 
                      hover:bg-[#2a2a2a] 
                      border-r 
                      border-gray-700
                      h-8
                       ${
                         colIndex === selectedDates.length - 1
                           ? "border-r-0"
                           : ""
                       }`}
                  >
                    {PollCreatorService.generateVisibleTimeSlots(
                      granularityMinutes,
                      false,
                    ).map((timeSlot) => (
                      <div
                        key={`${timeSlot.hour}-${timeSlot.minute}`}
                        className={`
                            w-16 
                            h-8
                            flex-shrink-0 
                            p-2 
                            text-xs 
                            text-gray-300 
                            flex items-center 
                            justify-center 
                            border-r 
                            border-gray-100
                            ${
                              Number(timeGranularity) >= 60
                                ? "min-h-[32px] p-1"
                                : "min-h-[24px] p-0.5"
                            }
                          `}
                        data-time-hour={timeSlot.hour}
                      >
                        {timeSlot.label}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* Corps de la grille - Créneaux par date */}
              <div className="flex flex-1">
                {PollCreatorService.generateVisibleTimeSlots(
                  granularityMinutes,
                  false,
                ).map((timeSlot) => (
                  <React.Fragment key={`${timeSlot.hour}-${timeSlot.minute}`}>
                    {selectedDates.map((dateStr) => (
                      <div
                        key={`${dateStr}-${timeSlot.hour}-${timeSlot.minute}`}
                        className="flex-1"
                        data-date={dateStr}
                        data-hour={timeSlot.hour}
                        data-minute={timeSlot.minute}
                        data-selected={
                          timeSlotsByDate[dateStr]?.find(
                            (s) =>
                              s.hour === timeSlot.hour &&
                              s.minute === timeSlot.minute,
                          )?.enabled
                        }
                        data-fused={getTimeSlotBlocks(dateStr).find(
                          (b) =>
                            b.start.hour <= timeSlot.hour &&
                            b.start.minute <= timeSlot.minute &&
                            b.end.hour > timeSlot.hour,
                        )}
                      >
                        {timeSlotsByDate[dateStr]?.find(
                          (s) =>
                            s.hour === timeSlot.hour &&
                            s.minute === timeSlot.minute,
                        )?.enabled ? (
                          <button
                            data-date={dateStr}
                            data-hour={timeSlot.hour}
                            data-minute={timeSlot.minute}
                            data-selected="true"
                            onClick={() =>
                              handleTimeSlotToggle(
                                dateStr,
                                timeSlot.hour,
                                timeSlot.minute,
                              )
                            }
                            className={`w-full ${Number(timeGranularity) >= 60 ? "min-h-[32px] p-1" : "min-h-[24px] p-0.5"} bg-blue-500 hover:bg-blue-600 text-white text-xs rounded flex items-center justify-center transition-colors`}
                            title={`${timeSlot.hour.toString().padStart(2, "0")}:${timeSlot.minute.toString().padStart(2, "0")}`}
                          >
                            <span className="text-xs">
                              {timeSlot.hour.toString().padStart(2, "0")}:
                              {timeSlot.minute.toString().padStart(2, "0")}
                            </span>
                          </button>
                        ) : (
                          <button
                            data-date={dateStr}
                            data-hour={timeSlot.hour}
                            data-minute={timeSlot.minute}
                            data-selected="false"
                            onClick={() =>
                              handleTimeSlotToggle(
                                dateStr,
                                timeSlot.hour,
                                timeSlot.minute,
                              )
                            }
                            className={`
                                w-full 
                                ${Number(timeGranularity) >= 60 ? "min-h-[32px] p-1" : "min-h-[24px] p-0.5"} 
                                bg-[#1e1e1e1] hover:bg-[#2a2a2a2] border border-gray-600 hover:border-blue-300 transition-colors
                                text-gray-400
                              `}
                            title={t("creator.date.addSlotTitle", {
                              time: `${timeSlot.hour.toString().padStart(2, "0")}:${timeSlot.minute.toString().padStart(2, "0")}`,
                            })}
                          >
                            <span className="text-xs">
                              {timeSlot.hour.toString().padStart(2, "0")}:
                              {timeSlot.minute.toString().padStart(2, "0")}
                            </span>
                          </button>
                        )}
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Bouton Afficher plus d'horaires */}
            <div className="p-3 bg-[#0a0a0a] border-t border-gray-700">
              <button
                onClick={() => {}}
                className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:border-blue-300 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>{t("creator.date.showMoreSlots")}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
